import { useUpdatePostalCode } from '@ecomm/checkout/shipping-hooks'
import { usStates } from '@ecomm/data-constants'
import { useLocale } from '@ecomm/data-hooks'
import { useMicroCopy } from '@ecomm/micro-copy'
import { useMemo } from 'react'

import { FormCheckbox } from '../FormCheckbox'
import { FormField } from '../FormField'
import { FormSection } from '../FormSection'
import { FormSelect } from '../FormSelect'
import { FormTextInput } from '../FormTextInput'
import { useUpdateCartShippingAddress } from './useUpdateCartShippingAddress'

/**
 * A port of the ShippingAddress component used on the jotai shipping page.
 * https://simplisafe.atlassian.net/browse/ECP-10079
 */
export function ShippingAddress() {
  const locale = useLocale()
  const countryOptions = useMemo(() => {
    return locale === 'en-GB' ? ['United Kingdom'] : ['United States']
  }, [locale])

  // TODO: wire up fetching/setting user shipping address, setting login success, and funneling returning customer address values to formik

  const microCopy = useMicroCopy()
  // Uppercase the postal code for UK
  useUpdatePostalCode(locale)

  useUpdateCartShippingAddress()

  return (
    <div data-component="ShippingAddress">
      <FormSection title="Shipping address">
        <fieldset className="rounded-base bg-neutral-light-50 p-4 pb-4 md:px-8 md:pt-8 border-0 mx-0">
          <legend className="sr-only">Shipping address</legend>
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-4 md:flex-row lg:gap-6">
              <FormField
                className="w-full grow"
                id="firstName"
                label="First Name*"
              >
                <FormTextInput id="firstName" name="firstName" />
              </FormField>
              <FormField
                className="w-full grow"
                id="lastName"
                label="Last Name*"
              >
                <FormTextInput id="lastName" name="lastName" />
              </FormField>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
              <FormField
                className="w-full grow"
                id="streetName"
                label="Address 1*"
              >
                <FormTextInput id="streetName" name="streetName" />
              </FormField>
              <FormField
                className="w-full grow"
                id="additionalStreetInfo"
                label="Address 2 (Optional)"
              >
                <FormTextInput
                  id="additionalStreetInfo"
                  name="additionalStreetInfo"
                  placeholder="Apt/Suite/Other"
                />
              </FormField>
            </div>
            <div className="flex flex-col gap-4 lg:w-full lg:flex-row lg:gap-6">
              <FormField className="w-full grow" id="city" label="City*">
                <FormTextInput id="city" name="city" />
              </FormField>
              <div className="grid w-full grow grid-cols-2 gap-4 lg:gap-6">
                {locale === 'en-US' ? (
                  <FormField className="w-full grow" id="state" label="State*">
                    <FormSelect
                      name="state"
                      options={usStates}
                      placeholder="Select One"
                    />
                  </FormField>
                ) : null}
                <FormField
                  className="w-full grow overflow-hidden"
                  id="postalCode"
                  label={`${microCopy['postal-code']}*`}
                >
                  <FormTextInput
                    id="postalCode"
                    maxLength={10}
                    name="postalCode"
                  />
                </FormField>
              </div>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
              <FormField className="w-full grow" id="country" label="Country*">
                <FormSelect
                  name="country"
                  options={countryOptions}
                  placeholder="Select one"
                />
              </FormField>
              <div className="flex w-full grow flex-col overflow-hidden">
                <FormField className="w-full" id="phone" label="Phone Number*">
                  <FormTextInput
                    id="phone"
                    maxLength={20}
                    name="phone"
                    type="text"
                  />
                </FormField>
                {locale === 'en-US' && (
                  <p
                    className="mb-6 ml-0 text-sm md:mb-0"
                    data-component="phone_legal_disclaimer"
                  >
                    You agree to be contacted at this phone number about your
                    order.
                    <a
                      className="ml-1 text-neutral-black"
                      href="/legal/privacy-policy"
                      rel="noreferrer"
                      target="_blank"
                    >
                      Privacy policy
                      <span className="sr-only"> (opens in new tab)</span>
                    </a>
                    .
                  </p>
                )}
              </div>
            </div>
          </div>
          <hr
            className={`border-neutral-medium-100 my-4 border border-b-0 border-solid`}
            data-component="Divider"
          />
          <FormField id="same-billing" labelClassName="hidden md:flex">
            <FormCheckbox
              className="text-base font-normal"
              label="My billing information is the same as my delivery information."
              name="sameAddress"
            />
          </FormField>
        </fieldset>
      </FormSection>
    </div>
  )
}
